import React, { useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { toast } from 'sonner';
import { shopApi } from '../../utils/shopApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Printer, Barcode as BarcodeIcon, Search } from 'lucide-react';

/**
 * Shop Barcode Label Generator — picks products from the SHOP inventory
 * (`shop_products`), lets admin set qty, prints an A4 sheet of Code-128 /
 * EAN-13 labels via `jsbarcode`.
 */
const ShopBarcodeLabelsPage = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [format, setFormat] = useState('CODE128');
  const [showPrice, setShowPrice] = useState(true);
  const [showName, setShowName] = useState(true);
  const sheetRef = useRef(null);

  useEffect(() => {
    shopApi.get('/shop/admin/products').then((r) => {
      setProducts(Array.isArray(r.data) ? r.data : (r.data.items || []));
    }).catch(() => toast.error('Failed to load shop products'));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.barcode || '').toLowerCase().includes(q)
    );
  }, [products, search]);

  const toggle = (id, checked) => setSelected((prev) => {
    const next = { ...prev };
    if (checked) next[id] = next[id] || 1;
    else delete next[id];
    return next;
  });
  const setQty = (id, qty) => setSelected((prev) => ({ ...prev, [id]: Math.max(1, parseInt(qty || '1', 10)) }));

  const labels = useMemo(() => {
    const out = [];
    for (const p of products) {
      const qty = selected[p.id];
      if (!qty) continue;
      const code = (p.barcode || '').trim() || p.sku || p.id.slice(0, 12);
      for (let i = 0; i < qty; i += 1) out.push({ id: `${p.id}-${i}`, product: p, code });
    }
    return out;
  }, [products, selected]);

  useEffect(() => {
    if (!sheetRef.current) return;
    const svgs = sheetRef.current.querySelectorAll('svg[data-code]');
    svgs.forEach((svg) => {
      const code = svg.getAttribute('data-code');
      try {
        let useFormat = format;
        if (useFormat === 'EAN13' && !/^\d{12,13}$/.test(code)) useFormat = 'CODE128';
        JsBarcode(svg, code, {
          format: useFormat,
          width: 1.4, height: 40, fontSize: 12, margin: 2, displayValue: true,
        });
      } catch { /* skip invalid codes */ }
    });
  }, [labels, format]);

  const totalLabels = labels.length;
  const selectedCount = Object.keys(selected).length;
  const selectAllVisible = () => setSelected((prev) => {
    const next = { ...prev };
    filtered.forEach((p) => { if (!next[p.id]) next[p.id] = 1; });
    return next;
  });
  const clearSelection = () => setSelected({});
  const priceOf = (p) => Number(p.online_price || p.pos_price || p.base_price || 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden" data-testid="shop-barcode-labels-header">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2">
            <BarcodeIcon className="w-7 h-7 text-primary" /> Shop barcode labels
          </h1>
          <p className="text-sm text-muted-foreground">Pick shop products, set label quantity, print a self-adhesive A4 sheet.</p>
        </div>
        <Button onClick={() => window.print()} disabled={totalLabels === 0} data-testid="shop-barcode-print-btn">
          <Printer className="w-4 h-4 mr-2" /> Print {totalLabels} label{totalLabels === 1 ? '' : 's'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 print:hidden">
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by name, SKU or barcode…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="shop-barcode-search" />
            </div>
            <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="shop-barcode-select-all">Select visible</Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="shop-barcode-clear">Clear</Button>
          </div>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-border" data-testid="shop-barcode-product-list">
            {filtered.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">No shop products match.</div>}
            {filtered.map((p) => {
              const checked = !!selected[p.id];
              return (
                <div key={p.id} className="py-2 flex items-center gap-3" data-testid={`shop-barcode-row-${p.id}`}>
                  <Checkbox checked={checked} onCheckedChange={(v) => toggle(p.id, !!v)} data-testid={`shop-barcode-check-${p.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {(p.barcode || p.sku) ? <span>Code: <code>{p.barcode || p.sku}</code></span> : <span className="italic">No barcode/SKU — will use short-id</span>}
                      {priceOf(p) ? <span> · RWF {priceOf(p).toLocaleString()}</span> : null}
                    </div>
                  </div>
                  {checked && (
                    <div className="flex items-center gap-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min={1} value={selected[p.id]} onChange={(e) => setQty(p.id, e.target.value)} className="h-8 w-16" data-testid={`shop-barcode-qty-${p.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-4 space-y-4">
          <h2 className="font-semibold">Label options</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Barcode format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="h-9" data-testid="shop-barcode-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CODE128">Code-128 (recommended)</SelectItem>
                  <SelectItem value="EAN13">EAN-13 (12–13 digit codes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-4 pb-1">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={showName} onCheckedChange={(v) => setShowName(!!v)} /> Show name</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={showPrice} onCheckedChange={(v) => setShowPrice(!!v)} /> Show price</label>
            </div>
          </div>
          <div className="text-sm text-muted-foreground pt-2 border-t border-border">
            <div><strong>{selectedCount}</strong> product{selectedCount === 1 ? '' : 's'} selected</div>
            <div><strong>{totalLabels}</strong> label{totalLabels === 1 ? '' : 's'} to print</div>
            <div className="text-xs mt-2">Sheet: 5 columns × 13 rows = 65 labels per A4 page. Extra labels flow to a second page.</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 print:p-0 print:border-0" data-testid="shop-barcode-sheet-wrapper">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 print:hidden">Preview</div>
        <div
          ref={sheetRef}
          className="mx-auto grid grid-cols-5 gap-x-0 gap-y-0 border border-border print:border-0"
          style={{ width: '210mm', padding: '10mm', minHeight: totalLabels ? '297mm' : '120px' }}
          data-testid="shop-barcode-sheet"
        >
          {labels.length === 0 && (
            <div className="col-span-5 text-center text-sm text-muted-foreground py-16 print:hidden">
              Pick a shop product on the left to preview labels here.
            </div>
          )}
          {labels.map((l) => (
            <div key={l.id} className="flex flex-col items-center justify-center text-center overflow-hidden"
              style={{ width: '38mm', height: '21.2mm', margin: '0 1mm 0 1mm' }}>
              {showName && <div className="text-[9px] font-medium leading-none truncate w-full">{l.product.name}</div>}
              <svg data-code={l.code} className="my-0.5" />
              {showPrice && priceOf(l.product) ? (
                <div className="text-[9px] font-semibold leading-none">RWF {priceOf(l.product).toLocaleString()}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="shop-barcode-sheet-wrapper"], [data-testid="shop-barcode-sheet-wrapper"] * { visibility: visible !important; }
          [data-testid="shop-barcode-sheet-wrapper"] { position: absolute; left: 0; top: 0; width: 100%; }
          @page { size: A4 portrait; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default ShopBarcodeLabelsPage;
